import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { AdminPanelPage } from '../../AdminPanelPage';
import { ApplicantGroupForm, createTitleEditor, createDescriptionEditor } from './ApplicantGroupForm';
import { usePost } from '../../../API';
import { FilterGroupToBooleanExprConverter } from '../../shared/utils/filterConverters';
import { BooleanExpr } from '@aidkitorg/types/lib/survey';
import { ApplicantGroupFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';

export const CreateApplicantGroupPage: React.FC = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  const createApplicantGroupApi = usePost('/api/admin_panel/applicant_groups/create');

  const getCurrentValues = () => {
    const nameInput = nameRef.current?.value || '';
    const descriptionInput = descriptionRef.current?.value || '';

    return {
      name: nameInput,
      description: descriptionInput
    };
  };

  const createApplicantGroup = async (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }): Promise<{ id: string }> => {
    // Convert the filter config to BooleanExpr format
    const filterBooleanExpr: BooleanExpr | undefined = FilterGroupToBooleanExprConverter.convertFromConfig(data.filters);

    // Validate that we have a valid filter expression
    if (!filterBooleanExpr) {
      throw new Error('Failed to convert filters to valid expression');
    }

    // Call the API to create the applicant group
    const response = await createApplicantGroupApi({
      name: data.name,
      description: data.description || null,
      filter: filterBooleanExpr
    });

    if (response && typeof response === 'object' && 'data' in response &&
        typeof response.data === 'object' && response.data && 'id' in response.data &&
        typeof response.data.id === 'string') {
      return { id: response.data.id };
    }

    // If the response doesn't have an ID, throw an error
    throw new Error('Failed to create applicant group: Invalid response from server');
  };

  const handleSubmit = async (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }) => {
    setIsSubmitting(true);

    try {
      const result = await createApplicantGroup(data);
      history.push(`/admin-panel/applicants/applicant-groups/${result.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      history.push('/admin-panel/applicants/applicant-groups');
    }
  };

  const titleEditor = createTitleEditor(nameRef, strings, '', showValidation, getCurrentValues);
  const descriptionEditor = createDescriptionEditor(descriptionRef, strings, '');

  const pageActionButtons = [
    <Button
      key="cancel"
      type="button"
      variant="secondary"
      disabled={isSubmitting}
      onClick={handleCancel}
    >
      {strings.cancel}
    </Button>,
    <Button
      key="create"
      type="submit"
      variant="primary"
      disabled={isSubmitting}
      loading={isSubmitting}
      form="applicant-group-form"
    >
      {strings.create_group}
    </Button>
  ];

  return (
    <AdminPanelPage
      title={titleEditor}
      description={descriptionEditor}
      breadcrumbs={[
        { label: L.admin_panel.nav.applicants, href: '/admin-panel/applicants' },
        { label: strings.title, href: '/admin-panel/applicants/applicant-groups' },
        { label: strings.create_group }
      ]}
      pageActionButtons={pageActionButtons}
    >
      <ApplicantGroupForm
        mode="create"
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        nameRef={nameRef}
        descriptionRef={descriptionRef}
        showValidation={showValidation}
        setShowValidation={setShowValidation}
      />
    </AdminPanelPage>
  );
};
